import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Chip, Stack, TextField } from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import {
  createOrganisationDepartment,
  createOrganisationDepartmentSuccess,
  createOrganisationDepartmentToStaff,
  getOrganisationDepartmentList,
  getOrganisationDepartmentToStaffList,
  getStaffList,
  updateOrganisationDepartment,
  updateOrganisationDepartmentSuccess,
} from "../../../store/actions";
import filterActiveStaff from "../../Common/staffActive";
import AppModal from "../../Common/AppModal";
import InfoCard from "../../Common/InfoCard/InfoCard";

export default function CreateEditDepartment({
  modal,
  setModal,
  toggle,
  isEdit,
  updateData,
}) {
  const dispatch = useDispatch();

  // Default department chip colours (indigo, matching this modal's accent).
  // Used when a department has none set yet so the chip is never blank.
  const DEFAULT_COLOUR_BG = "#EEF2FF";
  const DEFAULT_COLOUR_TEXT = "#4F46E5";

  const defaultData = {
    Name: "",
    Description: "",
    Parent___OrganisationDepartmentHierarchy_Id: "",
    CssColourBackground: DEFAULT_COLOUR_BG,
    CssColourText: DEFAULT_COLOUR_TEXT,
  };

  const [data, setData] = useState(defaultData);

  // Task 24456 - a department with nobody in it is half a job. Creating one
  // meant saving it, switching tab, and adding people one at a time; the people
  // are picked here and assigned the moment the department exists.
  const [staffIds, setStaffIds] = useState([]);

  useEffect(() => {
    dispatch(getStaffList());
  }, [dispatch]);

  useEffect(() => {
    if (modal && !isEdit) {
      setData(defaultData);
      setStaffIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, isEdit]);

  const { staffData } = useSelector((state) => ({
    staffData: state.Staff.staffList?.staff_list,
  }));

  const staffOptions = (
    !isEmpty(staffData) ? filterActiveStaff(staffData, staffIds) : []
  ).map((item) => ({
    Id: item.Id,
    label:
      item?.FullName ||
      [item?.FirstName, item?.LastName].filter(Boolean).join(" "),
  }));

  useEffect(() => {
    if (!isEdit) return;
    if (isEmpty(updateData)) return;
    setData({
      Id: updateData?.Id,
      Name: updateData?.Name,
      Description: updateData?.Description,
      Parent___OrganisationDepartmentHierarchy_Id:
        updateData?.Parent___OrganisationDepartmentHierarchy_Id,
      CssColourBackground: updateData?.CssColourBackground || DEFAULT_COLOUR_BG,
      CssColourText: updateData?.CssColourText || DEFAULT_COLOUR_TEXT,
    });
  }, [updateData, isEdit]);

  const handleCreateUpdate = (type) => {
    // check type
    if (type === "create") {
      dispatch(createOrganisationDepartment([data]));
    }
    if (type === "update") {
      dispatch(updateOrganisationDepartment([data]));
    }
  };

  const {
    createOrganisationDepartmentSuccessAPI,
    updateOrganisationDepartmentSuccessAPI,
    loadingCreateOrganisationDepartment,
    loadingUpdateOrganisationDepartment,
  } = useSelector((state) => ({
    createOrganisationDepartmentSuccessAPI:
      state.Staff.createOrganisationDepartmentSuccess,
    updateOrganisationDepartmentSuccessAPI:
      state.Staff.updateOrganisationDepartmentSuccess,
    loadingCreateOrganisationDepartment:
      state.Staff.loadingCreateOrganisationDepartment,
    loadingUpdateOrganisationDepartment:
      state.Staff.loadingUpdateOrganisationDepartment,
  }));
  const { organisationDepartmentListAPI } = useSelector((state) => ({
    organisationDepartmentListAPI:
      state.Staff.organisationDepartmentList?.organisationdepartment_list,
  }));

  const helperCallFreshData = () => {
    dispatch(getOrganisationDepartmentList());
  };

  useEffect(() => {
    if (isEmpty(createOrganisationDepartmentSuccessAPI)) return;
    if (createOrganisationDepartmentSuccessAPI?.status === 200) {
      toast.success(createOrganisationDepartmentSuccessAPI?.message);

      // The create response carries the new Id, so the assignments can go in
      // straight after rather than making the user go and find the department
      // again. The department itself is already saved at this point - if the
      // assignment call fails, that failure is reported on its own and the
      // department is not rolled back.
      const newId =
        createOrganisationDepartmentSuccessAPI?.OrganisationDepartment_Id;
      if (newId && staffIds.length) {
        dispatch(
          createOrganisationDepartmentToStaff(
            staffIds.map((id) => ({
              OrganisationDepartment_Id: newId,
              Staff_Id: id,
              FlagManager: "0",
              StartDateTime: new Date(),
            }))
          )
        );
        dispatch(getOrganisationDepartmentToStaffList());
      }

      helperCallFreshData();
      setModal(false);
    } else {
      toast.error(createOrganisationDepartmentSuccessAPI?.message);
    }
    dispatch(createOrganisationDepartmentSuccess([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrganisationDepartmentSuccessAPI]);

  useEffect(() => {
    if (isEmpty(updateOrganisationDepartmentSuccessAPI)) return;
    if (updateOrganisationDepartmentSuccessAPI?.status === 200) {
      toast.success(updateOrganisationDepartmentSuccessAPI?.message);
      helperCallFreshData();
      setModal(false);
    } else {
      toast.error(updateOrganisationDepartmentSuccessAPI?.message);
    }
    dispatch(updateOrganisationDepartmentSuccess([]));
  }, [updateOrganisationDepartmentSuccessAPI]);

  const saving =
    loadingCreateOrganisationDepartment || loadingUpdateOrganisationDepartment;

  const departmentOptions = organisationDepartmentListAPI || [];
  const selectedParent =
    departmentOptions.find(
      (item) => item.Id === data?.Parent___OrganisationDepartmentHierarchy_Id
    ) || null;

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={isEdit ? "Edit Department" : "Create New Department"}
      subtitle="Organise your team into departments and sub-departments"
      icon={<AccountTreeOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      loading={saving}
      loadingText={isEdit ? "Updating department…" : "Creating department…"}
      submitLabel={isEdit ? "Update" : "Create"}
      submitColor="success"
      submitDisabled={!data?.Name?.trim()}
      onSubmit={() => handleCreateUpdate(isEdit ? "update" : "create")}
    >
      <InfoCard title="Department details" icon="ri-building-line" accent="indigo">
        <Stack spacing={2.5}>
          <TextField
            label="Department Name"
            placeholder="e.g. Sales"
            value={data?.Name || ""}
            onChange={(e) => setData({ ...data, Name: e.target.value })}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Description"
            placeholder="What does this department do?"
            value={data?.Description || ""}
            onChange={(e) => setData({ ...data, Description: e.target.value })}
            fullWidth
            multiline
            minRows={3}
          />

          {/* Chip colours — shown wherever the department appears (kanban,
              tasks/jobs list). Live preview mirrors the rendered chip. */}
          <div>
            <span className="text-muted small d-block mb-2">Chip colour</span>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <span
                className="badge d-inline-flex align-items-center"
                style={{
                  background: data?.CssColourBackground || DEFAULT_COLOUR_BG,
                  color: data?.CssColourText || DEFAULT_COLOUR_TEXT,
                }}
              >
                <i className="ri-team-line me-1" />
                {data?.Name?.trim() || "Department"}
              </span>
              <label className="d-inline-flex align-items-center gap-2 m-0 small">
                <span>Background</span>
                <input
                  type="color"
                  aria-label="Department background colour"
                  value={data?.CssColourBackground || DEFAULT_COLOUR_BG}
                  onChange={(e) =>
                    setData({ ...data, CssColourBackground: e.target.value })
                  }
                  style={{
                    width: 34,
                    height: 34,
                    padding: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                  }}
                />
              </label>
              <label className="d-inline-flex align-items-center gap-2 m-0 small">
                <span>Text</span>
                <input
                  type="color"
                  aria-label="Department text colour"
                  value={data?.CssColourText || DEFAULT_COLOUR_TEXT}
                  onChange={(e) =>
                    setData({ ...data, CssColourText: e.target.value })
                  }
                  style={{
                    width: 34,
                    height: 34,
                    padding: 0,
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                  }}
                />
              </label>
            </Stack>
          </div>
        </Stack>
      </InfoCard>

      {!isEdit && (
        <InfoCard title="People" icon="ri-team-line" accent="violet">
          <Autocomplete
            multiple
            options={staffOptions}
            value={staffOptions.filter((o) => staffIds.includes(o.Id))}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, val) => option?.Id === val?.Id}
            onChange={(_e, options) =>
              setStaffIds((options || []).map((o) => o.Id))
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option.label}
                  {...getTagProps({ index })}
                  key={option.Id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign staff"
                placeholder="Search for people…"
                helperText="Optional — they can be added or changed later from Department To Staff."
              />
            )}
          />
        </InfoCard>
      )}

      <InfoCard title="Hierarchy" icon="ri-node-tree" accent="violet">
        <Autocomplete
          options={departmentOptions}
          value={selectedParent}
          getOptionLabel={(option) => option?.Name || ""}
          isOptionEqualToValue={(option, val) => option?.Id === val?.Id}
          onChange={(_e, option) =>
            setData({
              ...data,
              Parent___OrganisationDepartmentHierarchy_Id: option
                ? option.Id
                : "",
            })
          }
          renderOption={(props, option) => (
            <li {...props} key={option.Id}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <span>{option?.Name}</span>
                <Chip
                  size="small"
                  color={
                    option?.Parent___OrganisationDepartmentHierarchy_Id === null
                      ? "success"
                      : "primary"
                  }
                  variant="outlined"
                  label={
                    option?.Parent___OrganisationDepartmentHierarchy_Id === null
                      ? "Main Department"
                      : "Sub Department"
                  }
                />
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Parent department"
              placeholder="Select parent department (optional)"
              helperText="If this is a sub-department, pick its parent."
            />
          )}
        />
      </InfoCard>
    </AppModal>
  );
}
